import { collection, doc, setDoc, getDoc, getDocs, updateDoc, runTransaction, query, limit, orderBy, increment } from "firebase/firestore";
import { db } from '../utils/firebase.js';
import {arraysEqualIgnoreOrder} from "../utils/utils.js";


export const _deleteGameFromSession = async (groupId, sessionId, gamePlayedId) => {
    await runTransaction(db, async (transaction) => {
        var groupData = { memberPlayCount: {}, gamesPlayed: {} };

        // Trust no one. Get the game
        const playSessionRef = doc(db, "groups", groupId, "playSessions", sessionId);
        const playSessionDoc = await transaction.get(playSessionRef);
        const playSessionData = playSessionDoc.data();
        const gameData = playSessionData.games.find(game => game.gamePlayedId === gamePlayedId);
        
        if (!gameData.isCoop) {
            // find the winners and decrement their wins
            groupData.winners = {};
            for (const winner of gameData.winners) {
                groupData.winners[winner] = increment(-1);
            }
        }
        
        // find each player and decrement their plays
        for (const player of gameData.players) {
            groupData.memberPlayCount[player.member] = increment(-1);
        }
        
        // decrement the number of times this game was played
        groupData.gamesPlayed[gameData.game] = { count: increment(-1) };
        transaction.set(doc(db, "groups", groupId), groupData, { merge: true });
        
        // find the /group/{groupId}/gameSessions/{gameName} and decrement the count
        const gameSessionsRef = doc(db, "groups", groupId, "gameSessions", gameData.game);
        transaction.update(gameSessionsRef, { [sessionId]: increment(-1) });
        
        // delete the document at /groups/{groupId}/playSessions/{sessionId}/games[gameIndex]
        const gamesArray = playSessionData.games;
        const gameIndex = gamesArray.findIndex((game) => game['gamePlayedId'] == gameData.gamePlayedId);
        gamesArray[gameIndex].isDeleted = true;
        if (gamesArray.length === 0) {
            transaction.delete(playSessionRef);
        } else {
            transaction.update(playSessionRef, { games: gamesArray });
        }
    });
}

export const _getSessions = async (groupId, queryLimit) => {
    const playSessionsCollection = collection(db, "groups", groupId, "playSessions");
    const playSessionsQuery = await getDocs(query(playSessionsCollection, limit(queryLimit), orderBy("playDate", "desc")));
    const sessions = [];
    playSessionsQuery.forEach((doc) => {
        sessions.push(doc.id);
    });

    return sessions;
}

export const _getSpecificSession = async (groupId, sessionId) => {
    const playSessionDocRef = doc(db, "groups", groupId, "playSessions", sessionId);
    const playSessionDoc = await getDoc(playSessionDocRef);
    var playSessionData = playSessionDoc.data();
    return playSessionData;
}


export const _logGamePlayed = async (groupId, documentId, gameData) => {
    await runTransaction(db, async (transaction) => {
        const playSessionsCollection = collection(db, "groups", groupId, "playSessions");
        const playSessionDocRef = doc(playSessionsCollection, documentId);
        const playSessionDoc = await transaction.get(playSessionDocRef);
        const groupDocRef = doc(db, "groups", groupId);
        const groupDoc = await transaction.get(groupDocRef);

        gameData.gamePlayedId = `${gameData.game.substring(0, 3).toUpperCase()}_${Date.now().toString().substring(7)}`;
        gameData.isNew = true;
        gameData.game = gameData.game.trim();

        // First add or increment the game playSession with the winners
        if (!playSessionDoc.exists()) {
            await transaction.set(playSessionDocRef, { games: [gameData], playDate: new Date() });
        } else {
            const gamesArray = playSessionDoc.data().games;
            gamesArray.push(gameData);
            await transaction.update(playSessionDocRef, { games: gamesArray });
        }

        // Next, update the gamesPlayed and increment the number.
        const gamesPlayedMap = groupDoc.data().gamesPlayed || {};
        const winners = groupDoc.data().winners || {};
        const memberPlays = groupDoc.data().memberPlayCount || {};

        if (gamesPlayedMap[gameData.game]?.count) {
            gamesPlayedMap[gameData.game].count += 1;
        } else {
            gamesPlayedMap[gameData.game] = { count: 1 };
        }

        for (var i = 0; i < gameData.winners?.length; i++) {
            if (winners[gameData.winners[i]]) {
                winners[gameData.winners[i]] += 1;
            } else {
                winners[gameData.winners[i]] = 1;
            }
        }

        for (var i = 0; i < gameData.players.length; i++) {
            if (memberPlays[gameData.players[i].member]) {
                memberPlays[gameData.players[i].member] += 1;
            } else {
                memberPlays[gameData.players[i].member] = 1;
            }
        };

        await transaction.update(groupDocRef, { gamesPlayed: gamesPlayedMap, winners: winners, memberPlayCount: memberPlays });

        // update the /groups/{groupId/gameSessions/{gameName} with the session the new game can be found in
        // update the /groups/{groupId/gameSessions/{gameName} with the session the new game can be found in
        const gameSessionsRef = doc(db, "groups", groupId, "gameSessions", gameData.game);
        await setDoc(gameSessionsRef, { [documentId]: increment(1) }, { merge: true });
    });
}

export const _updatePreviousGame = async (groupId, sessionId, gamePlayedId, gameData, oldGameData) => {
    await runTransaction(db, async (transaction) => {
        // First get the /groups/{groupId}/playSessions/{sessionId} and replace the games at {gameIndex} with the new gameData
        const groupRef = doc(db, "groups", groupId);
        const playSessionRef = doc(db, "groups", groupId, "playSessions", sessionId);
        const playSessionDoc = await transaction.get(playSessionRef);
        const playSessionData = playSessionDoc.data();
        const gameIndex = playSessionData.games.findIndex((game) => game['gamePlayedId'] == gameData.gamePlayedId);
        playSessionData.games[gameIndex] = gameData;

        // set an isDirty bit for the trigger to know which game to review
        gameData.isDirty = true;

        // old was competitive and new is competitive
        let winnerData = { winners: {} };
        if (!oldGameData.isCoop && !gameData.isCoop) {
            // did the winner change?
            var winnerChanged = await !arraysEqualIgnoreOrder(oldGameData.winners, gameData.winners);
            if (winnerChanged) {
                // decrement the old winner count
                for (const winner of oldGameData.winners) {
                    winnerData.winners[winner] = increment(-1);
                }

                // increment the new winner count
                for (const winner of gameData.winners) {
                    winnerData.winners[winner] = increment(1);
                }

                await transaction.set(groupRef, winnerData, { merge: true });
            }
        } else if (oldGameData.isCoop && gameData.isCoop) {      // old was coop and new is coop
            delete gameData.winners;
        } else {
            // Not sure how we got here - game should not be able to change from Coop to Competitive (or vice versa)
            return;
        }

        await transaction.update(playSessionRef, { games: playSessionData.games });
    });
}
